import { ProfileTemplateFieldMode } from '@up2stay/up2promo-api-client';
import { Input, Skeleton } from '@up2stay/up2promo-ui';

import {
  getMemberCellValue,
  setMemberCellValue,
  type TemplateMemberValueColumn,
  type TemplateMemberValuesMap,
} from '@/core/lib/profile-template-member-values';
import type { MemberRosterRow } from '@/core/lib/profile-template-member-values-csv';

export type TemplateMemberValuesDict = {
  noTeams: string;
  noMembers: string;
  perMemberHint: string;
  defaultForAll: string;
  usesDefaultLabel: string;
  usesDefaultPlaceholder: string;
  searchPlaceholder: string;
  memberCount: string;
  filterAll: string;
  filterOverrides: string;
  filterNoOverrides: string;
  bulkEditHint: string;
  overrideCountLabel: string;
  pendingChangesCount: string;
  noSearchResults: string;
  editButton: string;
  editButtonTitle: string;
  enabledLabel: string;
  resetToDefault: string;
  tableColumnMember: string;
  tableColumnValue: string;
  tableCancel: string;
  tableDone: string;
  exportCsv: string;
  importCsv: string;
  importSuccess: string;
  importError: string;
  loadFailed: string;
  draftHint: string;
  syncingHint: string;
};

interface TemplateMemberValueInputsProps {
  column: TemplateMemberValueColumn;
  memberValues: TemplateMemberValuesMap;
  onMemberValuesChange: (next: TemplateMemberValuesMap) => void;
  roster: MemberRosterRow[];
  rosterLoading?: boolean;
  hasTeams: boolean;
  canManage: boolean;
  dict: TemplateMemberValuesDict;
  maxLength?: number;
  placeholder?: string;
}

export function TemplateMemberValueInputs({
  column,
  memberValues,
  onMemberValuesChange,
  roster,
  rosterLoading = false,
  hasTeams,
  canManage,
  dict,
  maxLength,
  placeholder,
}: TemplateMemberValueInputsProps) {
  const updateCell = (userId: string, value: string) => {
    const entry = setMemberCellValue(
      { ...(memberValues[userId] ?? {}) },
      column,
      value,
    );
    const hasData =
      entry.jobTitle != null ||
      entry.company != null ||
      entry.location != null ||
      (entry.socials && Object.keys(entry.socials).length > 0);
    const next = { ...memberValues };
    if (hasData) {
      next[userId] = entry;
    } else {
      delete next[userId];
    }
    onMemberValuesChange(next);
  };

  if (!hasTeams) {
    return <p className="text-xs text-muted-foreground">{dict.noTeams}</p>;
  }

  if (rosterLoading) {
    return <Skeleton className="h-16 w-full rounded-md" />;
  }

  if (roster.length === 0) {
    return <p className="text-xs text-muted-foreground">{dict.noMembers}</p>;
  }

  return (
    <div className="space-y-2 rounded-md border border-dashed border-primary/30 bg-primary/5 p-2.5">
      <p className="text-xs font-medium text-foreground">
        {dict.perMemberHint}
      </p>
      <ul className="space-y-1.5">
        {roster.map((member) => (
          <li key={member.userId} className="flex items-center gap-2 min-w-0">
            <span
              className="w-[7.5rem] shrink-0 truncate text-xs font-medium"
              title={
                member.email
                  ? `${member.displayName} (${member.email})`
                  : member.displayName
              }
            >
              {member.displayName}
            </span>
            <Input
              className="h-8 min-w-0 flex-1 text-sm"
              type="text"
              inputMode={
                placeholder?.trim().toLowerCase().startsWith('http')
                  ? 'url'
                  : undefined
              }
              autoComplete={
                placeholder?.trim().toLowerCase().startsWith('http')
                  ? 'url'
                  : undefined
              }
              autoCapitalize={
                placeholder?.trim().toLowerCase().startsWith('http') ||
                column.kind === 'social'
                  ? 'none'
                  : undefined
              }
              autoCorrect={
                placeholder?.trim().toLowerCase().startsWith('http') ||
                column.kind === 'social'
                  ? 'off'
                  : undefined
              }
              spellCheck={
                placeholder?.trim().toLowerCase().startsWith('http') ||
                column.kind === 'social'
                  ? false
                  : undefined
              }
              value={getMemberCellValue(memberValues[member.userId], column)}
              disabled={!canManage}
              maxLength={maxLength}
              placeholder={placeholder}
              onChange={(e) => updateCell(member.userId, e.target.value)}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

export function isIndividualFieldMode(
  mode: ProfileTemplateFieldMode | undefined,
): boolean {
  return mode === ProfileTemplateFieldMode.FREE;
}
